/**
 * Sub-pass 2B Item 3 — Broker survey gate.
 *
 * Polls ``GET /api/broker-survey/status`` after auth resolves; when the
 * server reports ``needs_survey: true`` we mount the mandatory modal
 * above the app tree. Backend is the source of truth for completion —
 * we never trust local storage.
 *
 * Skipped on public pages that must remain reachable while the survey
 * is pending: legal pages, auth callback, login/signup, owner console,
 * and the landing/pricing surfaces (an anonymous visitor cannot have a
 * survey to answer anyway; the gate checks ``user`` before running).
 */
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import BrokerSurveyModal from "@/components/BrokerSurveyModal";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PATH_ALLOWLIST_PREFIXES = [
  "/legal",
  "/de/",
  "/en/",
  "/login",
  "/signup",
  "/auth/callback",
  "/owner-console",
];

function pathIsAllowlisted(pathname) {
  if (!pathname) return false;
  return PATH_ALLOWLIST_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
}

export default function BrokerSurveyGate() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [needsSurvey, setNeedsSurvey] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { setNeedsSurvey(false); setChecked(true); return; }
    if (pathIsAllowlisted(location.pathname || "")) { setChecked(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/broker-survey/status");
        if (!cancelled) setNeedsSurvey(!!res?.data?.needs_survey);
      } catch (_) {
        if (!cancelled) setNeedsSurvey(false); // fail-open on transport error
      } finally {
        if (!cancelled) setChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user, loading, location.pathname]);

  if (loading || !checked || !user || !needsSurvey) return null;
  return <BrokerSurveyModal onCompleted={() => setNeedsSurvey(false)} />;
}
